import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useAssistenciaTecnicaListaPagination } from "../hooks/AssistenciaTecnica_lista/pagination";
import { initialAssistenciaTecnicaListaFiltersState, useAssistenciaTecnicaListaFilters } from "../hooks/AssistenciaTecnica_lista/filters";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { ClienteDropDown } from "../../../../common/domain/models/cliente_dropdown";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { CategoriaAssistenciaDropDown } from "../../domain/models/categoria_assistencia_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { AssistenciaTecnicaLista } from "../../domain/models/assistencia_tecnica_lista";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { ReportAssistenciaTecnicaForm } from "../schemas/AssistenciaTecnica_lista/report_assistencia_tecnica";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";

import dayjs from "dayjs";

/**
 * LoadAssistenciasTecnicas
 */

type LoadAssistenciasTecnicas = {
  idCliente?: number[] | null;
  cliente?: string | null;
  centroCustos?: number[] | null;
  local?: string | null;
  dataAberturaDe?: Date | null;
  dataAberturaAte?: Date | null;
  tipo?: number | null;
  dataEncerramentoDe?: Date | null;
  dataEncerramentoAte?: Date | null;
  codigo?: number | null;
  procedente?: number | null;
  categorias?: number[] | null;
  resolvido?: number | null;
  prioridade?: number | null;
  nroChamado?: string | null;
  status?: number | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useAssistenciaTecnicaListaViewModel 
 */

export const useAssistenciaTecnicaListaViewModel = () => {

  const {
    reports: {
      getReportAssistenciaTecnicaUseCase,
    },
    entregaObras: {
      getAssistenciaCategoriaDropDownUseCase,
      getAllAssistenciaTecnicaUseCase,
      deleteAssistenciaTecnicaUseCase,
    }
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
    loadClientesDropDown,
  } = useDropDownLoader();

  const { pushInfoNotification, pushErrorNotification, setPageSaving } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const debouncedHandlerRef = useCreateDebounceHandler(750);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [clientesDropdown, setClientesDropdown] = useState<ClienteDropDown[]>();
  const [centroCustosDropdown, setCentroCustosDropdown] = useState<CentroCustoDropDown[]>();
  const [categoriasDropdown, setCategoriasDropdown] = useState<CategoriaAssistenciaDropDown[]>();

  const [assistenciasTecnicas, setAssistenciasTecnicas] = useState<AssistenciaTecnicaLista[]>();

  const [selectedAssistenciaTecnica, setSelectedAssistenciaTecnica] = useState<AssistenciaTecnicaLista>();

  const [assistenciaTecnicaDeleteModal, setAssistenciaTecnicaDeleteModal] = useState(false);
  const [assistenciaTecnicaReportModal, setAssistenciaTecnicaReportModal] = useState(false);

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const filters = useAssistenciaTecnicaListaFilters();
  const pagination = useAssistenciaTecnicaListaPagination();

  /**
   * Funcoes de carregamento
   */

  const loadAssistenciaCategoriaDropDown = useCallback(async () => {
    try {
      const data = await getAssistenciaCategoriaDropDownUseCase.getAssistenciaCategoriaDropDown();
      setCategoriasDropdown(data);
    } catch (e: unknown) {
      setCategoriasDropdown(undefined);
      throw new Error('Falha ao carregar os dados de categoria de assistência técnica!');
    }
  }, [
    getAssistenciaCategoriaDropDownUseCase,
  ]);

  const loadAssistenciasTecnicas = useCallback(async (fs?: LoadAssistenciasTecnicas) => {
    try {
      const filteredIdCliente = evaluateParamValue(fs?.idCliente, filters.idCliente);
      const filteredCliente = evaluateParamValue(fs?.cliente, filters.cliente);
      const filteredCentroCustos = evaluateParamValue(fs?.centroCustos, filters.centroCustos);
      const filteredLocal = evaluateParamValue(fs?.local, filters.local);
      const filteredDataAberturaDe = evaluateParamValue(fs?.dataAberturaDe, filters.dataAberturaDe);
      const filteredDataAberturaAte = evaluateParamValue(fs?.dataAberturaAte, filters.dataAberturaAte);
      const filteredTipo = evaluateParamValue(fs?.tipo, filters.tipo);
      const filteredDataEncerramentoDe = evaluateParamValue(fs?.dataEncerramentoDe, filters.dataEncerramentoDe);
      const filteredDataEncerramentoAte = evaluateParamValue(fs?.dataEncerramentoAte, filters.dataEncerramentoAte);
      const filteredCodigo = evaluateParamValue(fs?.codigo, filters.codigo);
      const filteredProcedente = evaluateParamValue(fs?.procedente, filters.procedente);
      const filteredCategorias = evaluateParamValue(fs?.categorias, filters.categorias);
      const filteredResolvido = evaluateParamValue(fs?.resolvido, filters.resolvido);
      const filteredPrioridade = evaluateParamValue(fs?.prioridade, filters.prioridade);
      const filteredNroChamado = evaluateParamValue(fs?.nroChamado, filters.nroChamado);
      const filteredStatus = evaluateParamValue(fs?.status, filters.status);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllAssistenciaTecnicaUseCase.getAllAssistenciaTecnica(
        filteredIdCliente,
        filteredCliente,
        filteredCentroCustos,
        filteredLocal,
        filteredDataAberturaDe,
        filteredDataAberturaAte,
        filteredTipo,
        filteredDataEncerramentoDe,
        filteredDataEncerramentoAte,
        filteredCodigo,
        filteredProcedente,
        filteredCategorias,
        filteredResolvido,
        filteredPrioridade,
        filteredNroChamado,
        filteredStatus,
        filteredPage,
        filteredRowsPerPage,
      );

      setAssistenciasTecnicas(getAllAssistenciaTecnicaUseCase.items);
      pagination.setTotalItems(getAllAssistenciaTecnicaUseCase.totalItems);

    } catch (e: unknown) {
      setAssistenciasTecnicas(undefined);
      throw new Error('Falha ao carregar os dados de Assistência Técnica!');
    }
  }, [
    filters,
    pagination,
    getAllAssistenciaTecnicaUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {

      await loadAssistenciaCategoriaDropDown();

      await loadCentroCustoDropDown(dt => setCentroCustosDropdown(dt));

      await loadClientesDropDown(dt => setClientesDropdown(dt));

      await loadAssistenciasTecnicas();

    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadAssistenciaCategoriaDropDown();

      await loadCentroCustoDropDown(dt => setCentroCustosDropdown(dt));

      await loadClientesDropDown(dt => setClientesDropdown(dt));

      await loadAssistenciasTecnicas();

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAssistenciaCategoriaDropDown,
    loadCentroCustoDropDown,
    loadClientesDropDown,
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleAssistenciaTecnicaEditar = useCallback(async (idAssistenciaTecnica: number | null, openNewTab: boolean) => {
    if (idAssistenciaTecnica) {
      if (openNewTab) {
        window.open(`/AssistenciaTecnica_cadastro?id=${idAssistenciaTecnica}`, '_blank', 'noopener,noreferrer');
      } else {
        backOrNavigate(`/AssistenciaTecnica_cadastro?id=${idAssistenciaTecnica}`, { alternativeRoute: '/AssistenciaTecnica_lista' });
      }
    } else {
      if (openNewTab) {
        window.open(`/AssistenciaTecnica_cadastro`, '_blank', 'noopener,noreferrer');
      } else {
        backOrNavigate(`/AssistenciaTecnica_cadastro`, { alternativeRoute: '/AssistenciaTecnica_lista' });
      }
    }
  }, [
    backOrNavigate,
  ]);

  const handleAssistenciaTecnicaDeleteOpenState = useCallback(async (idAssistenciaTecnica: number | null, openState: boolean) => {
    if (openState) {
      if (idAssistenciaTecnica && assistenciasTecnicas) {
        setSelectedAssistenciaTecnica(assistenciasTecnicas.find(a => a.idAssistenciaTecnica === idAssistenciaTecnica));
      } else {
        setSelectedAssistenciaTecnica(undefined);
      }
    }
    setAssistenciaTecnicaDeleteModal(openState);
  }, [
    assistenciasTecnicas,
  ]);

  const handleAssistenciaTecnicaDeleteSubmit = useCallback(async () => {
    if (selectedAssistenciaTecnica) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteAssistenciaTecnicaUseCase.deleteAssistenciaTecnica(
          selectedAssistenciaTecnica.idAssistenciaTecnica,
        );

        await loadAssistenciasTecnicas();

        setSelectedAssistenciaTecnica(undefined);
        setAssistenciaTecnicaDeleteModal(false);

        pushInfoNotification('Sucesso', 'Registro excluído com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de cliente!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedAssistenciaTecnica,
    deleteAssistenciaTecnicaUseCase,
    loadAssistenciasTecnicas,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleAssistenciaTecnicaReportOpenState = useCallback(async (openState: boolean) => {
    setAssistenciaTecnicaReportModal(openState);
  }, [
  ]);

  const handleAssistenciaTecnicaReportSubmit = useCallback(async (form: ReportAssistenciaTecnicaForm) => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportAssistenciaTecnicaUseCase.getReportAssistenciaTecnica(
            requiredMimeType,
            form.codigo,
            form.nroChamado,
            form.status,
            form.dataAbertura,
            form.unidade,
            form.procedente,
            form.categoria,
            form.resolvido,
            form.dataEncerramento,
            form.clientes,
            form.centroCustos,
            form.prioridade,
          ),
        closeReportModalCallback: () => setAssistenciaTecnicaReportModal(false),
      });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de cliente!"));
    } finally {
      setLoading(false);
    }
  }, [
    getReportAssistenciaTecnicaUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaListaPagination.setState({ page: page + 1 });
      await loadAssistenciasTecnicas({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de cliente!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaListaPagination.setState({ page: 1, rowsPerPage });
      await loadAssistenciasTecnicas({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de cliente!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleIdClienteChange = useCallback(async (idCliente: number[] | null) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaListaFilters.setState({ idCliente, cliente: null });
      await loadAssistenciasTecnicas({ page: 1, idCliente, cliente: null });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de cliente!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleClienteChange = useCallback(async (cliente: string | null) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaListaFilters.setState({ cliente, idCliente: null });
      await loadAssistenciasTecnicas({ page: 1, cliente, idCliente: null });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de cliente!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleCentroCustosChange = useCallback(async (centroCustos: number[] | null) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaListaFilters.setState({ centroCustos });
      await loadAssistenciasTecnicas({ page: 1, centroCustos });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de centro de custos!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleLocalChange = useDebouncedCallback(debouncedHandlerRef, async (local: string | null) => {
    useAssistenciaTecnicaListaFilters.setState({ local });
    return async () => {
      try {
        setLoading(true);
        await loadAssistenciasTecnicas({ page: 1, local });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de local!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleDataAberturaChange = useDebouncedCallback(debouncedHandlerRef, async (de: Date | null, ate: Date | null) => {
    useAssistenciaTecnicaListaFilters.setState({ dataAberturaDe: de, dataAberturaAte: ate });
    return async () => {
      if (de === null || dayjs(de).isValid() || ate === null || dayjs(ate).isValid()) {
        try {
          setLoading(true);
          await loadAssistenciasTecnicas({ page: 1, dataAberturaDe: de, dataAberturaAte: ate });
        } catch (e: unknown) {
          pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de data de abertura!"));
        } finally {
          setLoading(false);
        }
      }
    };
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleTipoChange = useCallback(async (tipo: number | null) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaListaFilters.setState({ tipo });
      await loadAssistenciasTecnicas({ page: 1, tipo });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de tipo!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleDataEncerramentoChange = useDebouncedCallback(debouncedHandlerRef, async (de: Date | null, ate: Date | null) => {
    useAssistenciaTecnicaListaFilters.setState({ dataEncerramentoDe: de, dataEncerramentoAte: ate });
    return async () => {
      if (de === null || dayjs(de).isValid() || ate === null || dayjs(ate).isValid()) {
        try {
          setLoading(true);
          await loadAssistenciasTecnicas({ page: 1, dataEncerramentoDe: de, dataEncerramentoAte: ate });
        } catch (e: unknown) {
          pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de data de encerramento!"));
        } finally {
          setLoading(false);
        }
      }
    };
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleCodigoChange = useDebouncedCallback(debouncedHandlerRef, async (codigo: number | null) => {
    useAssistenciaTecnicaListaFilters.setState({ codigo });
    return async () => {
      try {
        setLoading(true);
        await loadAssistenciasTecnicas({ page: 1, codigo });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de código!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleProcedenteChange = useCallback(async (procedente: number | null) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaListaFilters.setState({ procedente });
      await loadAssistenciasTecnicas({ page: 1, procedente });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de procedente!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleCategoriasChange = useCallback(async (categorias: number[] | null) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaListaFilters.setState({ categorias });
      await loadAssistenciasTecnicas({ page: 1, categorias });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de categorias!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleResolvidoChange = useCallback(async (resolvido: number | null) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaListaFilters.setState({ resolvido });
      await loadAssistenciasTecnicas({ page: 1, resolvido });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de resolvido!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handlePrioridadeChange = useCallback(async (prioridade: number | null) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaListaFilters.setState({ prioridade });
      await loadAssistenciasTecnicas({ page: 1, prioridade });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de prioridade!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleNroChamadoChange = useDebouncedCallback(debouncedHandlerRef, async (nroChamado: string | null) => {
    useAssistenciaTecnicaListaFilters.setState({ nroChamado });
    return async () => {
      try {
        setLoading(true);
        useAssistenciaTecnicaListaFilters.setState({ nroChamado });
        await loadAssistenciasTecnicas({ page: 1, nroChamado });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de número de chamado!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleStatusChange = useCallback(async (status: number | null) => {
    try {
      setLoading(true);
      useAssistenciaTecnicaListaFilters.setState({ status });
      await loadAssistenciasTecnicas({ page: 1, status });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de número de chamado!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useAssistenciaTecnicaListaFilters.setState({ ...initialAssistenciaTecnicaListaFiltersState });
      await loadAssistenciasTecnicas({ ...initialAssistenciaTecnicaListaFiltersState, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar o filtro de data de abertura!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadAssistenciasTecnicas,
    pushErrorNotification,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    clientesDropdown: clientesDropdown ?? [],
    centroCustosDropdown: centroCustosDropdown ?? [],
    categoriasDropdown: categoriasDropdown ?? [],
    assistenciasTecnicas: assistenciasTecnicas ?? [],
    assistenciaTecnicaDeleteModal,
    assistenciaTecnicaReportModal,
    reportViewerState,
    filters,
    pagination,
    handleReload,
    handleAssistenciaTecnicaEditar,
    handleAssistenciaTecnicaDeleteOpenState,
    handleAssistenciaTecnicaDeleteSubmit,
    handleAssistenciaTecnicaReportOpenState,
    handleAssistenciaTecnicaReportSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleIdClienteChange,
    handleClienteChange,
    handleCentroCustosChange,
    handleLocalChange,
    handleDataAberturaChange,
    handleTipoChange,
    handleDataEncerramentoChange,
    handleCodigoChange,
    handleProcedenteChange,
    handleCategoriasChange,
    handleResolvidoChange,
    handlePrioridadeChange,
    handleNroChamadoChange,
    handleStatusChange,
    handleClearFilters,
  };
}